import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, BookMarked, LogOut, User as UserIcon, Menu, Sun, Moon, Monitor, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { usePageVisibilityBundle } from "@/lib/pageVisibility";
import { api } from "@/lib/api";
import { NAV } from "@/constants/testIds";
import { PLATFORM_META, normaliseSocialHref } from "@/lib/socials";
import { loadIconStyleSettings, iconWrapperProps } from "@/lib/iconStyles";
import MobileSearchOverlay from "@/components/MobileSearchOverlay";
import DesktopSearchDropdown from "@/components/DesktopSearchDropdown";
import FeedbackButton from "@/components/FeedbackButton";
import CommandPalette from "@/components/CommandPalette";
import TipJarCard from "@/components/TipJarCard";
import ThemeToggle from "@/components/ThemeToggle";
import { toast } from "sonner";
import { osBinding, matchesShortcut } from "@/lib/shortcuts";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  const vis = usePageVisibilityBundle();
  const isAdmin = user?.role === "admin";
  // Admins always see every nav link (with the hidden ones flagged in the
  // banner on the page itself); everyone else sees only the visible ones.
  const canSee = (key) => isAdmin || vis.visibility[key] !== false;
  const label = (key) => vis.labels[key];
  const isHidden = (key) => vis.visibility[key] === false;
  const [params] = useSearchParams();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [footer, setFooter] = useState({
    tagline: "Clinical Minimal Edition",
    copyright: "© {year} Hospice Synopsis. Story · Strategy · Soul.",
    socials: [],
    tip_jar: { enabled: false },
  });
  const [iconStyle, setIconStyle] = useState({ style: "monochrome", size: "md" });
  const [shortcuts, setShortcuts] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    api.get("/site-settings/footer").then(r => setFooter(r.data)).catch(() => {});
    loadIconStyleSettings().then(setIconStyle);
    api.get("/site-settings/keyboard-shortcuts").then(r => setShortcuts(r.data.shortcuts || {})).catch(() => {});
  }, []);

  // Admin-only shortcut: theme toggle. Binding is admin-configurable via the
  // "Keyboard shortcuts" tab (default Ctrl+Alt+D / Cmd+Alt+D).
  useEffect(() => {
    if (user?.role !== "admin" || !shortcuts) return;
    const cfg = shortcuts["theme-toggle"];
    if (!cfg?.enabled) return;
    const binding = osBinding(cfg);
    if (!binding) return;
    const onKey = (e) => {
      const t = e.target;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
      if (!matchesShortcut(e, binding)) return;
      e.preventDefault();
      const isDark = document.documentElement.classList.contains("dark");
      const next = isDark ? "light" : "dark";
      setThemeMode(next);
      toast(`Theme: ${next === "dark" ? "Dark" : "Light"} mode`, { duration: 1200 });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user, setThemeMode, shortcuts]);

  // Admin-only shortcut: command palette. Binding is admin-configurable
  // (default Cmd+K / Ctrl+K). Overrides the browser's location-bar-focus
  // default binding — an intentional trade-off since admins live in the
  // app more than typical readers.
  useEffect(() => {
    if (user?.role !== "admin" || !shortcuts) return;
    const cfg = shortcuts["command-palette"];
    if (!cfg?.enabled) return;
    const binding = osBinding(cfg);
    if (!binding) return;
    const onKey = (e) => {
      if (!matchesShortcut(e, binding)) return;
      e.preventDefault();
      setPaletteOpen((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user, shortcuts]);


  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center gap-6">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-md bg-sky-700 grid place-items-center text-white font-heading font-bold">H</div>
            <div className="hidden sm:block">
              <div className="font-heading font-semibold text-slate-900 leading-tight">Hospice Synopsis</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Education & Curriculum</div>
            </div>
          </Link>

          <DesktopSearchDropdown initialQuery={params.get("q") || ""} />

          {/* Below lg: a compact search icon that opens an inline slide-down overlay with live suggestions */}
          <button data-testid="nav-search-mobile"
                  onClick={() => setMobileSearchOpen(true)}
                  className="lg:hidden ml-auto p-2 rounded-md hover:bg-slate-100 text-slate-600"
                  aria-label="Open search">
            <Search className="w-5 h-5"/>
          </button>

          <nav className="hidden lg:flex items-center gap-1 text-sm">
            {vis.order.map((key) => {
              if (!canSee(key)) return null;
              const cls = `px-3 py-2 hover:text-slate-900 ${isHidden(key) ? "text-slate-400 italic" : "text-slate-600"}`;
              if (key === "home")
                return <Link key={key} to="/" data-testid={NAV.home} className={cls}>{label("home")}</Link>;
              if (key === "pathways")
                return <Link key={key} to="/pathways" data-testid={NAV.pathways} className={cls}>{label("pathways")}</Link>;
              if (key === "glossary")
                return <Link key={key} to="/glossary" className={cls}>{label("glossary")}</Link>;
              if (key === "tools_and_metrics")
                return <Link key={key} to="/tools-and-metrics" data-testid="nav-tools-and-metrics" className={cls}>{label("tools_and_metrics")}</Link>;
              if (key === "bookmarks" && user)
                return (
                  <Link key={key} to="/bookmarks" data-testid={NAV.bookmarks}
                        className={`px-3 py-2 hover:text-slate-900 inline-flex items-center gap-1 ${isHidden("bookmarks") ? "text-slate-400 italic" : "text-slate-600"}`}>
                    <BookMarked className="w-4 h-4" /> {label("bookmarks")}
                  </Link>
                );
              return null;
            })}
            {user?.role === "admin" && (
              <Link to="/admin" data-testid={NAV.admin} className="px-3 py-2 text-sky-700 font-medium hover:text-sky-900">Admin</Link>
            )}
          </nav>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid={NAV.userMenu} className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-slate-100">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-slate-200 grid place-items-center"><UserIcon className="w-4 h-4 text-slate-600"/></div>
                )}
                <span className="hidden md:block text-sm text-slate-700">{user.name?.split(" ")[0]}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-2 text-xs text-slate-500">
                  {user.email}
                  <div className="mt-0.5 uppercase tracking-widest text-[10px] text-sky-700">{user.role}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/profile">My profile</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/bookmarks">My bookmarks</Link></DropdownMenuItem>
                {user.role === "admin" && <DropdownMenuItem asChild><Link to="/admin">Admin dashboard</Link></DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid={NAV.themeMenu}>
                    {themeMode === "dark" ? <Moon className="w-4 h-4 mr-2"/> :
                     themeMode === "light" ? <Sun className="w-4 h-4 mr-2"/> :
                     <Monitor className="w-4 h-4 mr-2"/>}
                    Appearance
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent className="w-44">
                      <DropdownMenuItem data-testid={NAV.themeLight} onClick={() => setThemeMode("light")}>
                        <Sun className="w-4 h-4 mr-2"/> Light
                        {themeMode === "light" && <Check className="w-3.5 h-3.5 ml-auto text-sky-700"/>}
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={NAV.themeDark} onClick={() => setThemeMode("dark")}>
                        <Moon className="w-4 h-4 mr-2"/> Dark
                        {themeMode === "dark" && <Check className="w-3.5 h-3.5 ml-auto text-sky-700"/>}
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={NAV.themeSystem} onClick={() => setThemeMode("system")}>
                        <Monitor className="w-4 h-4 mr-2"/> Follow system
                        {themeMode === "system" && <Check className="w-3.5 h-3.5 ml-auto text-sky-700"/>}
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid={NAV.logout} onClick={logout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <ThemeToggle />
              <Link to="/login" data-testid={NAV.login}>
                <Button className="bg-slate-900 hover:bg-slate-700 text-white">
                  Sign in
                </Button>
              </Link>
            </>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-mobile-menu"
              className="lg:hidden p-2 rounded-md hover:bg-slate-100 text-slate-700"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5"/>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52" data-testid="nav-mobile-menu-content">
              {vis.order.map((key) => {
                if (!canSee(key)) return null;
                if (key === "home")
                  return <DropdownMenuItem key={key} asChild><Link data-testid="nav-mobile-home" to="/">{label("home")}</Link></DropdownMenuItem>;
                if (key === "pathways")
                  return <DropdownMenuItem key={key} asChild><Link data-testid="nav-mobile-pathways" to="/pathways">{label("pathways")}</Link></DropdownMenuItem>;
                if (key === "glossary")
                  return <DropdownMenuItem key={key} asChild><Link data-testid="nav-mobile-glossary" to="/glossary">{label("glossary")}</Link></DropdownMenuItem>;
                if (key === "tools_and_metrics")
                  return (
                    <DropdownMenuItem key={key} asChild>
                      <Link data-testid="nav-mobile-tools-and-metrics" to="/tools-and-metrics">{label("tools_and_metrics")}</Link>
                    </DropdownMenuItem>
                  );
                if (key === "bookmarks" && user)
                  return (
                    <DropdownMenuItem key={key} asChild>
                      <Link data-testid="nav-mobile-bookmarks" to="/bookmarks" className="inline-flex items-center gap-2">
                        <BookMarked className="w-4 h-4"/>{label("bookmarks")}
                      </Link>
                    </DropdownMenuItem>
                  );
                return null;
              })}
              <DropdownMenuSeparator/>
              <DropdownMenuItem asChild>
                <Link data-testid="nav-mobile-search" to="/search" className="inline-flex items-center gap-2">
                  <Search className="w-4 h-4"/>Search Site
                </Link>
              </DropdownMenuItem>
              {user?.role === "admin" && (
                <>
                  <DropdownMenuSeparator/>
                  <DropdownMenuItem asChild>
                    <Link data-testid="nav-mobile-admin" to="/admin" className="text-sky-700 font-medium">
                      Admin dashboard
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <MobileSearchOverlay open={mobileSearchOpen} onClose={() => setMobileSearchOpen(false)}/>

      <main>{children}</main>

      <footer className="border-t border-slate-200 mt-24 py-10 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          {footer.tip_jar?.enabled && (
            <div className="mb-8" data-testid="footer-tip-jar">
              <TipJarCard
                heading={footer.tip_jar.heading}
                body={footer.tip_jar.body}
                buttonLabel={footer.tip_jar.button_label}
                amountsCents={footer.tip_jar.amounts_cents}
                allowCustom={footer.tip_jar.allow_custom !== false}
              />
            </div>
          )}
          <div className="text-sm text-slate-500 flex flex-col md:flex-row justify-between gap-4 md:items-center">
          <div data-testid="footer-copyright-line">
            {(footer.copyright || "© {year} Hospice Synopsis. Story · Strategy · Soul.")
              .replace(/\{year\}/g, String(new Date().getFullYear()))}
          </div>
          <div className="flex items-center gap-4 md:justify-end flex-wrap">
            <FeedbackButton/>
            {footer.socials?.length > 0 && (
              <div data-testid="footer-socials" className="flex items-center gap-1">
                {footer.socials.map((s) => {
                  const meta = PLATFORM_META[s.platform];
                  if (!meta) return null;
                  const Icon = meta.Icon;
                  const wrap = iconWrapperProps({ platformKey: s.platform, styleName: iconStyle.style, sizeName: iconStyle.size });
                  return (
                    <a key={s.platform}
                       data-testid={`footer-social-${s.platform}`}
                       href={normaliseSocialHref(s.platform, s.url)}
                       target={s.platform === "email" ? undefined : "_blank"}
                       rel="noopener noreferrer"
                       aria-label={meta.label}
                       title={meta.label}
                       className={wrap.className}
                       style={wrap.style}>
                      <Icon className={wrap.iconSizeClass}/>
                    </a>
                  );
                })}
              </div>
            )}
            <div data-testid="footer-tagline" className="text-xs uppercase tracking-[0.2em]">
              {footer.tagline}
            </div>
          </div>
          </div>
        </div>
      </footer>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
